import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";
import session from "../Utils/session";

import Title from "../Components/Title";
import PlaylistCard from "../Components/PlaylistCard";
import CompactUserCard from "../Components/CompactUserCard";
import InviteModal from "../Components/InviteModal";
import Button from "../Components/Button";
import NotificationSettings from "../Components/NotificationSettings";
import editIcon from "../Assets/edit.svg";
import config from '../Utils/config';
import { useIsMobile } from "../hooks/useIsMobile";
import { usePlausible } from "../hooks/usePlausible";

// Format numbers to compact notation (1k, 13.3k, 1.2m, etc.)
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'm';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return num.toString();
};

// Stable 4-digit decoration derived from the username — purely cosmetic.
const idDecoration = (username) => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = ((hash << 5) - hash) + username.charCodeAt(i);
    hash |= 0;
  }
  return ('0000' + (Math.abs(hash) % 10000)).slice(-4);
};

const MyProfile = ({ MyProfileStore, PlaylistStore, onTogglePlaylist }) => {
  const navigate = useNavigate();
  const username = session.getUsername();
  const { trackEvent } = usePlausible();
  const [profilePictureNonce, setProfilePictureNonce] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    MyProfileStore.load();
  }, [MyProfileStore]);

  const handlePlaylistSelect = (loadPlaylistFn) => {
    loadPlaylistFn();
    if (isMobile && onTogglePlaylist) {
      onTogglePlaylist(false);
    }
  };

  const profile = MyProfileStore.profile;
  const playlistCount = profile?.playlists.length ?? 0;
  const followerCount = profile?.followedBy.length ?? 0;
  const followingCount = profile?.following.length ?? 0;

  return (
    <div className="flex-1 flex flex-col bg-primary">
      {/* Hardware ID Card header */}
      <div className="border-b border-neutral-800 bg-gradient-to-b from-neutral-950 to-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
          {/* Top row: avatar + identity + actions */}
          <div className="flex flex-wrap items-stretch gap-4">
            <ProfilePic
              src={`${config.serverUrl}/users/profile/${username}/avatar${
                profilePictureNonce > 0 ? `?nonce=${profilePictureNonce}` : ""
              }`}
              onEdit={(imageFile) => {
                MyProfileStore.uploadProfilePic(imageFile).then(() => {
                  trackEvent('Upload Profile Picture');
                  setProfilePictureNonce(profilePictureNonce + 1);
                });
              }}
            />

            {/* Identity */}
            <div className="flex-1 flex flex-col justify-center min-w-0 py-1">
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-[0.3em] text-text-muted font-semibold mb-1.5 font-mono">
                  User · ID {idDecoration(username)}
                </div>
                <h1 className="font-mono text-xl md:text-2xl font-bold text-text leading-none truncate">
                  {username}
                </h1>
                {profile && (
                  <div className="text-[11px] text-text-muted mt-2 font-mono tabular-nums">
                    {playlistCount} {playlistCount === 1 ? 'playlist' : 'playlists'}
                    <span className="mx-1.5 text-text-dim">·</span>
                    {formatNumber(profile.playCount)} {profile.playCount === 1 ? 'play' : 'plays'}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-row gap-2 shrink-0 self-start">
              <Button
                onClick={() => {
                  trackEvent('Open Invite Modal');
                  MyProfileStore.openInviteModal();
                }}
                variant="primary"
                size="xs"
              >
                Invite
              </Button>
              <Button
                onClick={() => {
                  trackEvent('Logout');
                  session.clear();
                  navigate("/");
                }}
                variant="ghost"
                size="xs"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Stats meters */}
          {profile && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Meter label="Plays" value={profile.playCount} accent="blue" />
              <Meter label="Followers" value={followerCount} accent="emerald" />
              <Meter label="Following" value={followingCount} accent="purple" />
            </div>
          )}
        </div>
      </div>

      {/* Content Sections */}
      {profile && (
        <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-5 md:py-6 space-y-6 md:space-y-7">
          {/* My Playlists */}
          <section>
            <Title
              eyebrow="Collection"
              label="My Playlists"
              subtitle={`${playlistCount} ${playlistCount === 1 ? 'playlist' : 'playlists'}`}
            />
            {profile.playlists.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3">
                {profile.playlists.map((list) => (
                  <PlaylistCard
                    title={list.title}
                    key={list.title}
                    thumbnails={list.thumbnailUrls}
                    onSelect={() => handlePlaylistSelect(() => PlaylistStore.loadPlaylist(list.id))}
                  />
                ))}
              </div>
            ) : (
              <EmptyState>You have no playlists yet</EmptyState>
            )}
          </section>

          {/* Followers */}
          <section>
            <Title
              eyebrow="Network"
              label="Followers"
              subtitle={`${followerCount} ${followerCount === 1 ? 'person' : 'people'}`}
            />
            {profile.followedBy.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {profile.followedBy.map((user) => (
                  <CompactUserCard
                    username={user.username}
                    key={user.username}
                    online={user.online}
                    image={`${config.serverUrl}/users/profile/${user.username}/avatar`}
                    onSelect={() => navigate(`/app/u/${user.username}`)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState>Nobody is following you yet</EmptyState>
            )}
          </section>

          {/* Following */}
          <section>
            <Title
              eyebrow="Network"
              label="Following"
              subtitle={`${followingCount} ${followingCount === 1 ? 'person' : 'people'}`}
            />
            {profile.following.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {profile.following.map((user) => (
                  <CompactUserCard
                    username={user.username}
                    key={user.username}
                    online={user.online}
                    image={`${config.serverUrl}/users/profile/${user.username}/avatar`}
                    onSelect={() => navigate(`/app/u/${user.username}`)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState>You are not following anybody yet</EmptyState>
            )}
          </section>

          {/* Notification Settings */}
          <section>
            <Title eyebrow="System" label="Notifications" />
            <NotificationSettings />
          </section>
        </div>
      )}

      <InviteModal />
    </div>
  );
};

const ACCENT_GLOWS = {
  blue: '0 0 12px rgba(59,130,246,0.35)',
  emerald: '0 0 12px rgba(52,211,153,0.32)',
  purple: '0 0 12px rgba(192,132,252,0.32)',
};
const ACCENT_TEXT = {
  blue: 'text-blue-300',
  emerald: 'text-emerald-300',
  purple: 'text-purple-300',
};

const Meter = ({ label, value, accent = 'blue' }) => (
  <div className="bg-gradient-to-b from-neutral-900 to-neutral-950 border border-neutral-800 rounded-md px-3 py-2 flex items-center justify-between gap-3 shadow-inner">
    <div className="text-[9px] uppercase tracking-[0.18em] text-text-muted font-semibold">{label}</div>
    <div
      className={`font-mono text-xl md:text-2xl ${ACCENT_TEXT[accent]} tabular-nums leading-none`}
      style={{ textShadow: ACCENT_GLOWS[accent] }}
    >
      {formatNumber(value)}
    </div>
  </div>
);

const EmptyState = ({ children }) => (
  <div className="flex items-center justify-center p-8 bg-secondary border border-dashed border-neutral-800 rounded-md">
    <p className="text-text-muted text-sm text-center">{children}</p>
  </div>
);

const ProfilePic = ({ src, onEdit }) => {
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleChange = (e) => {
      if (e.target.files && e.target.files[0]) {
        onEdit(e.target.files[0]);
      }
    };

    const inputElement = fileInputRef.current;
    if (inputElement) {
      inputElement.addEventListener("change", handleChange);
      return () => {
        inputElement.removeEventListener("change", handleChange);
      };
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="relative shrink-0 group">
      <div className="relative w-20 h-20 md:w-24 md:h-24 bg-neutral-900 border border-neutral-700 rounded-md overflow-hidden shadow-inner">
        <img
          src={src}
          alt="Profile"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Corner brackets */}
        <span className="pointer-events-none absolute top-1 left-1 w-2.5 h-2.5 border-t border-l border-blue-500/60" aria-hidden />
        <span className="pointer-events-none absolute top-1 right-1 w-2.5 h-2.5 border-t border-r border-blue-500/60" aria-hidden />
        <span className="pointer-events-none absolute bottom-1 left-1 w-2.5 h-2.5 border-b border-l border-blue-500/60" aria-hidden />
        <span className="pointer-events-none absolute bottom-1 right-1 w-2.5 h-2.5 border-b border-r border-blue-500/60" aria-hidden />
      </div>

      {onEdit && (
        <button
          onClick={() => fileInputRef.current.click()}
          className="absolute -bottom-1.5 -right-1.5 w-6 h-6 bg-neutral-800 border border-neutral-700 hover:bg-neutral-700 rounded-md flex items-center justify-center transition-all duration-150 shadow-md focus:outline-none focus:ring-1 focus:ring-accent"
          aria-label="Edit profile picture"
        >
          <img
            src={editIcon}
            alt=""
            className="w-3 h-3 opacity-80"
            onError={() => console.log("Edit icon not found")}
          />
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".png,.jpg,.jpeg"
          />
        </button>
      )}
    </div>
  );
};

export default inject("MyProfileStore", "PlaylistStore")(observer(MyProfile));
